/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import isNil from 'lodash/isNil';
import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../utils/StringUtils';
import TruncateWithTooltip from '../TruncateWithTooltip';
import StyledProps from '../theme/AppThemeInterface';

const ITEM_HEIGHT = 20;
const ITEM_CONTAINER_HEIGHT = 28;

const CollapseGroupItemContainer = styled.div`
  width: 100%;
  min-height: ${ITEM_CONTAINER_HEIGHT}px;
  display: flex;
`;
const CollapseGroupItemLabel = styled.div`
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  font-weight: normal;
  letter-spacing: 0px;
  line-height: ${ITEM_HEIGHT}px;
  font-size: 14px;
  color: ${({ labelColor, theme }: StyledProps & { labelColor?: string }) =>
    labelColor ? labelColor : theme.collapseGroupTextColor};
  min-height: ${ITEM_HEIGHT}px;
  display: inline-block;
  flex-grow: 1;
`;

type ItemValueProps = StyledProps & { color?: string };
const CollapseGroupItemValue = styled.div`
  font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
  letter-spacing: 0px;
  line-height: ${ITEM_HEIGHT}px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ color, theme }: ItemValueProps) => (color ? color : theme.collapseGroupTextColor)};
  min-height: ${ITEM_HEIGHT}px;
  position: relative;
  text-align: right;
  display: inline-block;
  max-width: 63%;
  flex-grow: 3;
`;
export interface CollapseGroupItemProps {
  id: string;
  label: string | JSX.Element;
  value: string | number | boolean | undefined | null | ReactElement;
  truncate?: boolean;
  hidden?: boolean;
  tooltipMaxWidth?: number;
  color?: string;
  labelColor?: string;
  valueFormatter?: Function;
  signalBars?: React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> | null;
}

const CollapseGroupItem = (props: CollapseGroupItemProps): ReactElement | null => {
  const { id, label, value, valueFormatter, signalBars, truncate, tooltipMaxWidth, color, labelColor, hidden } = props;

  if (hidden) {
    return null;
  }

  let valToShow = valueFormatter ? valueFormatter(value) : value;
  // return the string placeholder in the case the value is any falsy value
  valToShow = isNil(valToShow) || valToShow === '' ? EMPTY_FIELD_STRING_PLACEHOLDER : valToShow;

  const ItemValue = () => {
    let item = valToShow;
    if (truncate) {
      item = <TruncateWithTooltip text={valToShow} tooltipMaxWidthPx={tooltipMaxWidth} xOffsetPx={-70} />;
    } else if (signalBars) {
      item = signalBars;
    }

    return item;
  };
  return (
    <CollapseGroupItemContainer>
      <CollapseGroupItemLabel data-test={`collapse-group-item__label--${id}`} labelColor={labelColor}>
        {label}
      </CollapseGroupItemLabel>
      <CollapseGroupItemValue data-test={`collapse-group-item--${id}`} color={color}>
        {ItemValue()}
      </CollapseGroupItemValue>
    </CollapseGroupItemContainer>
  );
};
export default CollapseGroupItem;
